import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { client } from './lib/api'
import { Avatar, Logo, NavIcon, useHashRoute } from './lib/ui'
import { Landing, HowItWorks } from './components/Landing'
import { AuthPage } from './components/Auth'
import { ForgotPasswordPage, ResetPasswordPage, VerifyEmailPage } from './components/PasswordFlow'
import { LegalPage } from './components/Legal'
import { Discover } from './components/Discover'
import { Search } from './components/Search'
import { Likes, type LikedProfile } from './components/Likes'
import { Messages, type ChatDraft } from './components/Messages'
import { Wallet } from './components/Wallet'
import { Settings, type MeUser } from './components/Settings'
import { Profile } from './components/Profile'
import { NotificationsModal } from './components/NotificationsModal'
import { Admin } from './components/Admin'
import type { PublicProfile } from '../shared/types'
import { OnboardingWizard } from './components/OnboardingWizard'
import type { ProfileCompletionSnapshot } from '../shared/onboarding'
import { initAdMob } from './lib/admob'
import { isNativeApp, onNativeOAuthComplete } from './lib/nativeAuth'
import { setNativeSessionToken } from './lib/nativeSession'
import { captureAffiliateRefFromLocation } from './lib/affiliate'
import { useKeyboardInset, useScrollFocusedFieldIntoView } from './lib/mobileUx'
import { LIVE_POLL_MS, useLiveRefresh } from './lib/live'

type AppView = 'discover' | 'search' | 'likes' | 'messages' | 'wallet' | 'profile' | 'settings' | 'admin'

const APP_VIEWS: AppView[] = [
  'discover',
  'search',
  'likes',
  'messages',
  'wallet',
  'profile',
  'settings',
  'admin',
]

type ToastTone = 'ok' | 'err' | 'info'
type ToastItem = { id: number; message: string; tone: ToastTone }

function App() {
  const [route, navigate] = useHashRoute()
  const [booting, setBooting] = useState(true)
  const [user, setUser] = useState<MeUser | null>(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [environment, setEnvironment] = useState('production')
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const [chatDraft, setChatDraft] = useState<ChatDraft | null>(null)
  const [matchPeer, setMatchPeer] = useState<PublicProfile | null>(null)
  const [unread, setUnread] = useState(0)
  const [adHtml, setAdHtml] = useState<string | null>(null)
  const [photoUrl, setPhotoUrl] = useState<string | null>(null)
  const [onboarding, setOnboarding] = useState<ProfileCompletionSnapshot | null>(null)
  const [authMethod, setAuthMethod] = useState<'password' | 'google'>('password')
  const [chatImmersive, setChatImmersive] = useState(false)
  const [creditBalance, setCreditBalance] = useState<number | null>(null)
  const [notifOpen, setNotifOpen] = useState(false)
  const [notifUnread, setNotifUnread] = useState(0)
  const [notifPulse, setNotifPulse] = useState(false)
  const toastId = useRef(0)
  const lastNotifUnread = useRef(0)

  useKeyboardInset(true)
  useScrollFocusedFieldIntoView(true)

  useEffect(() => {
    captureAffiliateRefFromLocation()
  }, [route])

  const notify = useCallback((msg: string, tone: ToastTone = 'info') => {
    const id = ++toastId.current
    setToasts((list) => [...list.slice(-2), { id, message: msg, tone }])
    window.setTimeout(() => {
      setToasts((list) => list.filter((t) => t.id !== id))
    }, tone === 'err' ? 4500 : 3200)
  }, [])

  const isDev = environment !== 'production'

  const refreshMe = useCallback(async () => {
    const res = await client.me()
    if (res.user) {
      const u = res.user as unknown as MeUser
      setUser(u)
      setIsAdmin(!!res.isAdmin)
      setAuthMethod(res.authMethod === 'google' ? 'google' : 'password')
      setOnboarding(res.onboarding ?? null)
      return true
    }
    setUser(null)
    setIsAdmin(false)
    setAuthMethod('password')
    setOnboarding(null)
    return false
  }, [])

  useEffect(() => {
    if (!isNativeApp()) return
    return onNativeOAuthComplete(async (result) => {
      if (result.error) return
      const ok = await refreshMe()
      if (ok) navigate(result.dest ?? '/discover')
    })
  }, [refreshMe, navigate])

  useEffect(() => {
    void (async () => {
      try {
        const cfg = await client.publicConfig()
        setEnvironment(cfg.environment ?? 'production')
      } catch {
        /* offline-tolerant boot */
      }
      try {
        await refreshMe()
      } catch {
        setNativeSessionToken(null)
      }
      setBooting(false)
    })()
  }, [refreshMe])

  useEffect(() => {
    if (booting || user || !isNativeApp()) return
    if (route === '/' || route === '' || route.startsWith('/how-it-works')) {
      navigate('/signin')
    }
  }, [booting, user, route, navigate])

  const refreshLive = useCallback(() => {
    if (!user) return
    Promise.all([client.conversations('matches'), client.conversations('requests')])
      .then(([m, r]) => setUnread([...m.conversations, ...r.conversations].reduce((s, c) => s + c.unread, 0)))
      .catch(() => undefined)
    client
      .notifications()
      .then((n) => {
        const prev = lastNotifUnread.current
        setNotifUnread(n.unread)
        if (n.unread > prev) {
          setNotifPulse(true)
          window.setTimeout(() => setNotifPulse(false), 1200)
          const newest = n.notifications.find((x) => !x.readAt)
          if (newest && !notifOpen) {
            notify(newest.body || newest.title, newest.type === 'match' ? 'ok' : 'info')
          }
        }
        lastNotifUnread.current = n.unread
      })
      .catch(() => undefined)
    client
      .wallet()
      .then((w) => setCreditBalance(w.platformCredits))
      .catch(() => undefined)
  }, [user, notifOpen, notify])

  useLiveRefresh(refreshLive, LIVE_POLL_MS, !!user)

  useEffect(() => {
    if (!user) return
    client
      .profileDetail(user.id)
      .then((d) => setPhotoUrl(d.photos.find((p) => p.kind === 'public' && p.urlPath)?.urlPath ?? null))
      .catch(() => undefined)
  }, [user, route])

  useEffect(() => {
    if (!user) return
    void initAdMob()
  }, [user])

  useEffect(() => {
    if (!user || !isDev) return
    client
      .ads('search_grid')
      .then((r) => setAdHtml(r.ad?.html ?? null))
      .catch(() => undefined)
  }, [user, isDev])

  const view: AppView = useMemo(() => {
    const seg = route.replace(/^\//, '').split('/')[0] as AppView
    return APP_VIEWS.includes(seg) ? seg : 'discover'
  }, [route])

  const openChatWith = useCallback(
    (peer: { id: string; displayName: string; accentColor: string }) => {
      setChatDraft({ peerId: peer.id, peerName: peer.displayName, color: peer.accentColor })
      navigate('/messages')
    },
    [navigate],
  )

  const logout = useCallback(async () => {
    try {
      await client.logout()
    } catch {
      /* session may already be gone */
    }
    setUser(null)
    setIsAdmin(false)
    navigate(isNativeApp() ? '/signin' : '/')
  }, [navigate])

  const bottomNav: Array<{ view: AppView; label: string; icon: 'discover' | 'favorites' | 'messages' | 'profile'; badge?: number }> = [
    { view: 'discover', label: 'discover', icon: 'discover' },
    { view: 'likes', label: 'favorites', icon: 'favorites' },
    { view: 'messages', label: 'messages', icon: 'messages', badge: unread || undefined },
    { view: 'profile', label: 'profile', icon: 'profile' },
  ]

  if (booting) {
    return (
      <div className="boot-screen" role="status" aria-live="polite" aria-busy="true">
        <Logo compact />
        <div className="spinner" />
        <p>Loading PairVelle…</p>
      </div>
    )
  }

  if (!isNativeApp() && route.startsWith('/how-it-works')) return <HowItWorks />
  if (route.startsWith('/legal/')) return <LegalPage doc={route.split('/')[2] ?? 'privacy'} />

  if (!user) {
    if (route.startsWith('/forgot-password'))
      return <ForgotPasswordPage onDone={() => navigate('/signin')} />
    if (route.startsWith('/reset-password'))
      return <ResetPasswordPage onDone={() => navigate('/signin')} />
    if (route.startsWith('/verify-email'))
      return <VerifyEmailPage onDone={() => navigate('/signin')} />
    if (route.startsWith('/signin'))
      return (
        <AuthPage
          mode="signin"
          onDone={async (dest) => {
            await refreshMe()
            navigate(dest ?? '/onboarding')
          }}
        />
      )
    if (route.startsWith('/signup'))
      return (
        <AuthPage
          mode="signup"
          onDone={async (dest) => {
            await refreshMe()
            navigate(dest ?? '/onboarding')
          }}
        />
      )
    if (isNativeApp()) {
      return (
        <AuthPage
          mode="signin"
          onDone={async (dest) => {
            await refreshMe()
            navigate(dest ?? '/onboarding')
          }}
        />
      )
    }
    return <Landing />
  }

  const needsOnboarding = !isAdmin && !(onboarding?.complete ?? false)

  if (needsOnboarding) {
    return (
      <>
        <OnboardingWizard
          user={user}
          initialCompletion={onboarding}
          onComplete={() => void refreshMe().then(() => navigate('/discover'))}
          onLogout={logout}
          notify={notify}
        />
        <div className="toast-stack" aria-live="polite">
          {toasts.map((t) => (
            <div key={t.id} className={`toast toast--${t.tone}`} role="status" onClick={() => setToasts((list) => list.filter((x) => x.id !== t.id))}>
              {t.message}
            </div>
          ))}
        </div>
      </>
    )
  }

  return (
    <main className={`app-shell${chatImmersive ? ' app-shell--chat-immersive' : ''}`}>
      <header className="app-header">
        <Logo compact onClick={() => navigate('/discover')} />
        {user.city && (
          <div className="location-pill">
            <span>●</span> {user.city}
          </div>
        )}
        <div className="account-actions">
          {isDev && <span className="dev-badge">DEV</span>}
          <button
            className="balance-button"
            type="button"
            onClick={() => navigate('/wallet')}
            aria-label="Wallet and credits"
          >
            <small>Credits</small>
            <strong>{creditBalance != null ? creditBalance : '—'}</strong>
          </button>
          <button
            className={`icon-button${notifOpen ? ' active' : ''}${notifPulse ? ' is-pulse' : ''}`}
            type="button"
            onClick={() => setNotifOpen(true)}
            aria-label={notifUnread ? `${notifUnread} notifications` : 'Notifications'}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path
                fill="currentColor"
                d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22Zm8-6v-5a8 8 0 1 0-16 0v5l-2 2v1h20v-1l-2-2Z"
              />
            </svg>
            {notifUnread > 0 ? <i className="icon-badge">{notifUnread > 9 ? '9+' : notifUnread}</i> : null}
          </button>
          <button
            className={`icon-button${view === 'settings' ? ' active' : ''}`}
            type="button"
            onClick={() => navigate('/settings')}
            aria-label="Settings"
            aria-current={view === 'settings' ? 'page' : undefined}
          >
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
              <path
                fill="currentColor"
                d="M19.14 12.94c.04-.31.06-.63.06-.94s-.02-.63-.06-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39.96a7.07 7.07 0 0 0-1.63-.94l-.36-2.54A.5.5 0 0 0 14.9 2h-3.8a.5.5 0 0 0-.5.42l-.36 2.54c-.58.22-1.12.53-1.63.94l-2.39-.96a.5.5 0 0 0-.6.22L3.7 8.48a.5.5 0 0 0 .12.64l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94L3.82 14.52a.5.5 0 0 0-.12.64l1.92 3.32c.14.24.43.34.68.24l2.39-.96c.5.41 1.05.73 1.63.94l.36 2.54c.05.24.26.42.5.42h3.8c.24 0 .45-.18.5-.42l.36-2.54c.58-.21 1.13-.53 1.63-.94l2.39.96c.25.1.54 0 .68-.24l1.92-3.32a.5.5 0 0 0-.12-.64l-2.03-1.58ZM12 15.5A3.5 3.5 0 1 1 12 8.5a3.5 3.5 0 0 1 0 7Z"
              />
            </svg>
          </button>
          <button className="avatar-button" onClick={() => navigate('/profile')} aria-label="Profile">
            {photoUrl ? (
              <img src={photoUrl} alt="" />
            ) : (
              user.display_name.slice(0, 1).toUpperCase()
            )}
          </button>
        </div>
      </header>

      <section className="app-content">
        {view === 'discover' && (
          <Discover
            onOpenChat={(p) => openChatWith(p)}
            onMatch={(p) => setMatchPeer(p)}
            notify={notify}
            onUpgrade={() => navigate('/wallet')}
          />
        )}
        {view === 'search' && (
          <Search onOpenChat={(p) => openChatWith(p)} notify={notify} adHtml={isDev ? adHtml : null} />
        )}
        {view === 'likes' && (
          <Likes
            onOpenChat={(p: LikedProfile) =>
              openChatWith({ id: p.id, displayName: p.displayName, accentColor: p.accentColor })
            }
            onMatch={(p) => setMatchPeer(p)}
            notify={notify}
          />
        )}
        {view === 'messages' && (
          <Messages
            meId={user.id}
            draft={chatDraft}
            clearDraft={() => setChatDraft(null)}
            notify={notify}
            onThreadActive={setChatImmersive}
          />
        )}
        {view === 'wallet' && <Wallet isDev={isDev} notify={notify} />}
        {view === 'profile' && (
          <Profile
            user={user}
            onSaved={() => {
              void refreshMe()
              void client
                .profileDetail(user.id)
                .then((d) =>
                  setPhotoUrl(
                    [...d.photos]
                      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                      .find((p) => p.kind === 'public' && p.urlPath)?.urlPath ?? null,
                  ),
                )
                .catch(() => undefined)
            }}
            notify={notify}
          />
        )}
        {view === 'settings' && (
          <Settings
            user={user}
            authMethod={authMethod}
            onSaved={() => void refreshMe()}
            onLogout={logout}
            onNavigate={(v) => navigate(`/${v}`)}
            notify={notify}
          />
        )}
        {view === 'admin' && isAdmin && <Admin notify={notify} />}
      </section>

      <nav className="bottom-nav" aria-label="Primary">
        {bottomNav.map((item) => (
          <button
            key={item.view}
            className={view === item.view ? 'active' : ''}
            onClick={() => navigate(`/${item.view}`)}
          >
            <NavIcon name={item.icon} />
            <span>{item.label}</span>
            {item.badge ? <i>{item.badge}</i> : null}
          </button>
        ))}
      </nav>

      {matchPeer && (
        <div className="modal-backdrop" onClick={() => setMatchPeer(null)}>
          <div className="modal match-pop" onClick={(e) => e.stopPropagation()} role="dialog">
            <h2>Mutual interest</h2>
            <div className="match-avatars">
              <Avatar name={user.display_name} color={user.accent_color} size={76} />
              <Avatar name={matchPeer.displayName} color={matchPeer.accentColor} size={76} />
            </div>
            <p>You and {matchPeer.displayName} are connected — message free.</p>
            <div className="modal-actions">
              <button
                className="button primary"
                onClick={() => {
                  const peer = matchPeer
                  setMatchPeer(null)
                  openChatWith(peer)
                }}
              >
                Say hello
              </button>
              <button className="button secondary" onClick={() => setMatchPeer(null)}>
                Keep browsing
              </button>
            </div>
          </div>
        </div>
      )}

      <NotificationsModal
        open={notifOpen}
        onClose={() => setNotifOpen(false)}
        onNavigate={(path) => navigate(path)}
        notify={notify}
        onUnreadChange={setNotifUnread}
      />

      <div className="toast-stack" aria-live="polite">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`toast toast--${t.tone}`}
            role="status"
            onClick={() => setToasts((list) => list.filter((x) => x.id !== t.id))}
          >
            {t.message}
          </div>
        ))}
      </div>
    </main>
  )
}

export default App
